import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface ModelProfile {
  id: string;
  legalName: string;
  stageNames: string;
  dateOfBirth: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string | null;
  email: string | null;
  verificationStatus: string;
}

interface IdDocument {
  id: string;
  documentType: string;
  expirationDate: string | null;
  verificationStatus: string;
}

export default function MyProfile() {
  const { user, refreshAuth } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [stageNamesInput, setStageNamesInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backFileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<Partial<ModelProfile>>({});
  const [idUpload, setIdUpload] = useState({
    documentType: 'drivers_license',
    frontImage: null as File | null,
    backImage: null as File | null,
    expirationDate: '',
  });

  // Fetch profile if model has one
  const { data: profileData, isLoading, error: profileError } = useQuery({
    queryKey: ['myProfile', user?.modelProfileId],
    queryFn: () => api.get<{ model: ModelProfile; identificationDocuments: IdDocument[] }>(`/models/${user?.modelProfileId}`),
    enabled: !!user?.modelProfileId,
  });

  const profile = profileData?.data.model;
  const idDocs = profileData?.data.identificationDocuments || [];

  // All hooks must be called before any early returns
  // Create profile mutation
  const createProfileMutation = useMutation({
    mutationFn: (data: any) => api.post('/models', data),
    onSuccess: async () => {
      setSuccess('Profile created successfully!');
      setError('');
      // Refresh auth to get updated user with modelProfileId
      await refreshAuth();
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      setIsEditing(false);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create profile');
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (data: any) => api.put(`/models/${user?.modelProfileId}`, data),
    onSuccess: () => {
      setSuccess('Profile updated successfully!');
      setError('');
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
      setIsEditing(false);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    },
  });

  // Upload ID mutation
  const uploadIdMutation = useMutation({
    mutationFn: async (data: { documentType: string; frontImage: File; backImage?: File; expirationDate?: string }) => {
      const formData = new FormData();
      formData.append('documentType', data.documentType);
      formData.append('frontImage', data.frontImage);
      if (data.backImage) {
        formData.append('backImage', data.backImage);
      }
      if (data.expirationDate) {
        formData.append('expirationDate', data.expirationDate);
      }
      return api.upload(`/models/${user?.modelProfileId}/ids`, formData);
    },
    onSuccess: () => {
      setSuccess('ID uploaded successfully! Pending verification.');
      setError('');
      setIdUpload({ documentType: 'drivers_license', frontImage: null, backImage: null, expirationDate: '' });
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to upload ID');
    },
  });

  // Show error if profile fetch failed
  if (profileError && user?.modelProfileId) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>My Profile</h1>
        </div>
        <div className="error-message">
          Failed to load profile: {profileError instanceof Error ? profileError.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const stageNames = stageNamesInput
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const data = {
      ...formData,
      stageNames,
      address: {
        line1: formData.addressLine1,
        line2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
        country: formData.country || 'United States',
      },
    };

    if (profile) {
      updateProfileMutation.mutate(data);
    } else {
      createProfileMutation.mutate(data);
    }
  };

  const handleUploadId = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!idUpload.frontImage) {
      setError('Please select a front image of your ID');
      return;
    }

    uploadIdMutation.mutate({
      documentType: idUpload.documentType,
      frontImage: idUpload.frontImage,
      backImage: idUpload.backImage || undefined,
      expirationDate: idUpload.expirationDate || undefined,
    });
  };

  const startEditing = () => {
    if (profile) {
      setFormData(profile);
      const stageNames = typeof profile.stageNames === 'string'
        ? JSON.parse(profile.stageNames || '[]')
        : profile.stageNames;
      setStageNamesInput(Array.isArray(stageNames) ? stageNames.join(', ') : '');
    }
    setIsEditing(true);
  };

  if (isLoading) {
    return <div className="page"><div className="loading">Loading profile...</div></div>;
  }

  // No profile yet - show creation form
  if (!profile && !user?.modelProfileId) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Complete Your Profile</h1>
        </div>

        <div className="card">
          <div className="alert alert-info">
            Please complete your profile to participate in productions and sign documents.
          </div>

          <form onSubmit={handleSaveProfile} className="form">
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="form-section">
              <h3>Personal Information</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="legalName">Legal Name *</label>
                  <input
                    type="text"
                    id="legalName"
                    value={formData.legalName || ''}
                    onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                    required
                    placeholder="Full legal name as it appears on your ID"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth *</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    value={formData.dateOfBirth || ''}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="stageNames">Stage Names</label>
                <input
                  type="text"
                  id="stageNames"
                  value={stageNamesInput}
                  onChange={(e) => setStageNamesInput(e.target.value)}
                  placeholder="Comma-separated stage names (optional)"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email || user?.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Address *</h3>

              <div className="form-group">
                <label htmlFor="addressLine1">Street Address</label>
                <input
                  type="text"
                  id="addressLine1"
                  value={formData.addressLine1 || ''}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="addressLine2">Apt, Suite, etc.</label>
                <input
                  type="text"
                  id="addressLine2"
                  value={formData.addressLine2 || ''}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    type="text"
                    id="city"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <input
                    type="text"
                    id="state"
                    value={formData.state || ''}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="postalCode">ZIP Code</label>
                  <input
                    type="text"
                    id="postalCode"
                    value={formData.postalCode || ''}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={createProfileMutation.isPending}>
                {createProfileMutation.isPending ? 'Saving...' : 'Create Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Has profile - show profile view/edit
  return (
    <div className="page">
      <div className="page-header">
        <h1>My Profile</h1>
        {!isEditing && (
          <button onClick={startEditing} className="btn btn-secondary">
            Edit Profile
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="detail-grid">
        <div className="card">
          <h2>Profile Information</h2>

          {isEditing ? (
            <form onSubmit={handleSaveProfile} className="form">
              <div className="form-row">
                <div className="form-group">
                  <label>Legal Name</label>
                  <input
                    type="text"
                    value={formData.legalName || ''}
                    onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    value={formData.dateOfBirth || ''}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Stage Names</label>
                <input
                  type="text"
                  value={stageNamesInput}
                  onChange={(e) => setStageNamesInput(e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  value={formData.addressLine1 || ''}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.state || ''}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>ZIP</label>
                  <input
                    type="text"
                    value={formData.postalCode || ''}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setIsEditing(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={updateProfileMutation.isPending}>
                  {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div className="detail-content">
              <div className="detail-row">
                <span className="detail-label">Legal Name</span>
                <span className="detail-value">{profile?.legalName}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date of Birth</span>
                <span className="detail-value">{profile?.dateOfBirth}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Stage Names</span>
                <span className="detail-value">
                  {(() => {
                    const names = typeof profile?.stageNames === 'string'
                      ? JSON.parse(profile?.stageNames || '[]')
                      : profile?.stageNames;
                    return Array.isArray(names) && names.length > 0 ? names.join(', ') : '-';
                  })()}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email</span>
                <span className="detail-value">{profile?.email || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Phone</span>
                <span className="detail-value">{profile?.phone || '-'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Address</span>
                <span className="detail-value">
                  {profile?.addressLine1}
                  {profile?.addressLine2 && `, ${profile.addressLine2}`}
                  <br />
                  {profile?.city}, {profile?.state} {profile?.postalCode}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Verification Status</span>
                <span className={`badge ${profile?.verificationStatus === 'verified' ? 'badge-success' : 'badge-warning'}`}>
                  {profile?.verificationStatus}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <h2>Identification Documents</h2>

          {idDocs.length > 0 ? (
            <div className="id-docs-list">
              {idDocs.map((doc) => (
                <div key={doc.id} className="id-doc-item">
                  <div className="id-doc-type">{doc.documentType.replace('_', ' ')}</div>
                  <div className="id-doc-status">
                    <span className={`badge ${doc.verificationStatus === 'verified' ? 'badge-success' : 'badge-warning'}`}>
                      {doc.verificationStatus}
                    </span>
                  </div>
                  {doc.expirationDate && (
                    <div className="id-doc-expiry">Expires: {doc.expirationDate}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="alert alert-warning">
              No ID documents uploaded. Please upload a valid government-issued ID.
            </div>
          )}

          <hr />

          <h3>Upload ID Document</h3>
          <form onSubmit={handleUploadId} className="form">
            <div className="form-group">
              <label>Document Type</label>
              <select
                value={idUpload.documentType}
                onChange={(e) => setIdUpload({ ...idUpload, documentType: e.target.value })}
              >
                <option value="drivers_license">Driver's License</option>
                <option value="passport">Passport</option>
                <option value="state_id">State ID</option>
                <option value="military_id">Military ID</option>
              </select>
            </div>

            <div className="form-group">
              <label>Front of ID *</label>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={(e) => setIdUpload({ ...idUpload, frontImage: e.target.files?.[0] || null })}
              />
              {idUpload.frontImage && <small>Selected: {idUpload.frontImage.name}</small>}
            </div>

            <div className="form-group">
              <label>Back of ID (if applicable)</label>
              <input
                type="file"
                ref={backFileInputRef}
                accept="image/*"
                onChange={(e) => setIdUpload({ ...idUpload, backImage: e.target.files?.[0] || null })}
              />
              {idUpload.backImage && <small>Selected: {idUpload.backImage.name}</small>}
            </div>

            <div className="form-group">
              <label>Expiration Date</label>
              <input
                type="date"
                value={idUpload.expirationDate}
                onChange={(e) => setIdUpload({ ...idUpload, expirationDate: e.target.value })}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={uploadIdMutation.isPending}>
              {uploadIdMutation.isPending ? 'Uploading...' : 'Upload ID'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
